import React from 'react';
import 'twin.macro';
import { StaticImage } from 'gatsby-plugin-image';

import Layout from '~components/Layout';
import SEO from '~components/SEO';
import CardLink from '~components/Card/CardLink';
import Heading from '~components/Heading';
import Text from '~components/Text';
import { ExternalLink, InternalLink } from '~components/Link';

// Logos
import Logo from '~components/Svg/Logo';
import CNHLogo from '~components/Svg/CNHLogo';
import LinkLogo from '~components/Svg/LinkLogo';
import SSCHLogo from '~components/Svg/SSCHLogo';

// Bubbles
import GraphBubble from '~components/Svg/GraphBubble';
import HeartBubble from '~components/Svg/HeartBubble';
import WavingBubble from '~components/Svg/WavingBubble';

import { fsbgimg, roundedImg } from '~theme';
import { VARIANT } from '../enums';

const IndexPage: React.FC = () => {
  return (
    <Layout>
      <SEO />
      <div tw="relative pt-72">
        <div tw="relative z-20">
          <div tw="container">
            <Logo tw="w-full max-w-3xl h-auto mb-6" />
            <Heading level="h1" tw="text-white text-8xl mb-0">
              A Year of Resilience
            </Heading>
          </div>
          <div tw="bg-blue bg-opacity-95 w-full py-6">
            <div tw="container">
              <p tw="text-white text-5xl font-bold my-0">2020 - 2021 Impact Report</p>
            </div>
          </div>
        </div>
        <div tw="absolute inset-0 z-10 bg-gradient-to-r from-black to-transparent"></div>
        <StaticImage
          src="../images/senior-and-patient.jpg"
          alt=""
          layout="fullWidth"
          style={fsbgimg}
        />
      </div>
      <div tw="w-3/4 max-w-5xl mx-auto px-12 md:(w-full px-6)">
        <div tw="my-12 grid gap-12 md:(gap-32 grid-cols-3 items-center)">
          <CNHLogo />
          <LinkLogo />
          <SSCHLogo />
        </div>
      </div>
      <div tw="container">
        <div tw="grid my-24 md:(grid-cols-3 gap-8) lg:(grid-cols-4 gap-24)">
          <div tw="md: col-span-2 lg:col-span-3">
            <Heading level="h2" tw="mt-0 text-6xl">
              <span tw="text-black">A Year of</span> <span tw="text-blue">Resilience</span>
            </Heading>
            <Text>We live in times of crisis. Unfortunately, we aren’t facing only one.</Text>
            <Text>
              Opioid overdose. Youth violence. Hunger. Unemployment. Homelessness. And of course,
              the one affecting us all: COVID-19.
            </Text>
            <Text>But during times of crisis, great innovation can arise. And arise it did.</Text>
            <Text>
              To help vulnerable people in Toronto, our staff, volunteers, partners and participants
              themselves worked together to bring new services and projects to life. Whether it was
              introducing peer workers into COVID recovery hotels, or newcomer women sharing their
              stories about life during COVID-19, the impact of these programs was felt far and
              wide.
            </Text>
            <Text>Here are just a few of those programs, and the people we help:</Text>
            <ul tw="ml-3 list-inside list-disc text-lg">
              <li>
                <InternalLink to="/community-development#coping-with-covid" tw="text-purple-900">
                  Coping with COVID
                </InternalLink>
              </li>
              <li>
                <InternalLink to="/children-youth#enhanced-game-changes" tw="text-purple-900">
                  Enhanced Game Changers
                </InternalLink>
              </li>
              <li>
                <InternalLink to="/independent-living-seniors#food-bank-hampers" tw="text-purple-900">
                  Food Bank Hampers
                </InternalLink>
              </li>
              <li>
                <InternalLink to="/urban-health-homelessness-services#kensington" tw="text-purple-900">
                  Kensington Market Overdose Prevention Site
                </InternalLink>
              </li>
              <li>
                <InternalLink to="/independent-living-seniors#mobile-covid-vaccination-clinic" tw="text-purple-900">
                  Mobile COVID Vaccination Clinic
                </InternalLink>
              </li>
              <li>
                <InternalLink to="/housing-development#modular-support" tw="text-purple-900">
                  Modular Supportive Housing
                </InternalLink>
              </li>
              <li>
                <InternalLink to="/newcomer-services#rainbow-connection" tw="text-purple-900">
                  Rainbow Connect
                </InternalLink>
              </li>
            </ul>
            <Text>
              <InternalLink to="/leadership-report/" tw="text-purple-900">
                View our Full Leadership Report
              </InternalLink>
            </Text>
            <Heading level="h3" tw="text-blue">
              Our Mission
            </Heading>
            <Text>
              The Neighbourhood Group serves people at every stage of their lives, promotes
              independence and dignity, and engages the skills and talents of our neighbours to
              build a vibrant community.
            </Text>
            <div tw="flex space-x-3">
              <Text>
                <InternalLink to="/programs-outcomes" tw="text-purple-900">
                  View our Services &amp; Programs
                </InternalLink>
              </Text>
              {/* <Text>
                <InternalLink to="/financials" tw="text-purple-900">View our Financials</InternalLink>
              </Text> */}
            </div>
          </div>
          <div tw="col-span-3 md:col-span-1 flex flex-row md:flex-col space-x-6 space-y-0 md:(space-y-6 space-x-0)">
            <figure tw="flex flex-col space-y-3">
              <StaticImage style={roundedImg} src="../images/bill-leader.jpg" alt="" />
              <figcaption>
                <span tw="block font-bold">Bill Sinclair</span>
                <span tw="block italic text-gray-700">President and Chief Executive Officer</span>
              </figcaption>
            </figure>
            <figure tw="flex flex-col space-y-3">
              <StaticImage style={roundedImg} src="../images/samira-leader.jpg" alt="" />
              <figcaption>
                <span tw="block font-bold">Samira Viswanathan</span>
                <span tw="block italic text-gray-700">Co-Chair, Board of Directors</span>
              </figcaption>
            </figure>
            <figure tw="flex flex-col space-y-3">
              <StaticImage style={roundedImg} src="../images/sonia-leader.jpg" alt="" />
              <figcaption>
                <span tw="block font-bold">Sonia Yung</span>
                <span tw="block italic text-gray-700">Co-Chair, Board of Directors</span>
              </figcaption>
            </figure>
          </div>
        </div>
        <Heading level="h2" tw="text-6xl text-black">
          Services &amp; Programs
        </Heading>
        <div tw="grid gap-12 my-24 md:(grid-cols-2 justify-center) lg:(grid-cols-3 grid-rows-5)">
          <div tw="md:(col-span-1 row-span-1) lg:(col-span-1 row-span-1)">
            <CardLink
              variant={VARIANT.BLUE}
              to="/independent-living-seniors"
              title="Independent Living & Seniors"
            >
              <StaticImage style={fsbgimg} src="../images/senior-by-flowers.jpg" alt="" />
            </CardLink>
          </div>
          <div tw="md:(col-span-1 row-span-1) lg:(col-span-2 row-span-1)">
            <CardLink
              variant={VARIANT.BLUE}
              to="/housing-development"
              title="Housing & Housing Development"
            >
              <StaticImage style={fsbgimg} src="../images/woman-and-dog.jpg" alt="" />
            </CardLink>
          </div>
          <div tw="md:(col-span-1 row-span-1) lg:(col-span-2 row-span-1)">
            <CardLink
              variant={VARIANT.BLUE}
              to="/urban-health-homelessness-services"
              title="Urban Health & Homelessness Services"
            >
              <StaticImage style={fsbgimg} src="../images/guy-with-blue-hat.jpg" alt="" />
            </CardLink>
          </div>
          <div tw="md:(col-span-1 row-span-1) lg:(col-span-1 row-span-1)">
            <CardLink variant={VARIANT.GREEN} to="/children-youth" title="Children & Youth">
              <StaticImage style={fsbgimg} src="../images/women-with-a-red-shirt.jpg" alt="" />
            </CardLink>
          </div>
          <div tw="md:(col-span-1 row-span-1) lg:(col-span-1 row-span-2)">
            <CardLink variant={VARIANT.GREEN} to="/childcare" title="Child Care">
              <StaticImage style={fsbgimg} src="../images/little-girl-happy.jpg" alt="" />
            </CardLink>
          </div>
          <div tw="md:(col-span-1 row-span-1) lg:(col-span-2 row-span-1)">
            <CardLink variant={VARIANT.BLUE} to="/newcomer-services" title="Newcomer Services">
              <StaticImage style={fsbgimg} src="../images/giving-out-a-test-kit.jpg" alt="" />
            </CardLink>
          </div>
          <div tw="md:(col-span-1 row-span-1) lg:(col-span-1 row-span-1)">
            <CardLink variant={VARIANT.BLUE} to="/employment-services" title="Employment Services">
              <StaticImage style={fsbgimg} src="../images/person-with-hard-hat.jpg" alt="" />
            </CardLink>
          </div>
          <div tw="md:(col-span-1 row-span-1) lg:(col-span-1 row-span-1)">
            <CardLink
              variant={VARIANT.GREEN}
              to="/community-development"
              title="Community Development"
            >
              <StaticImage style={fsbgimg} src="../images/women-on-video-call.jpg" alt="" />
            </CardLink>
          </div>
          <div tw="md:(col-span-1 row-span-1) lg:(col-span-2 row-span-1)">
            <CardLink
              variant={VARIANT.BLUE}
              to="/conflict-resolution-training"
              title="Conflict Resolution & Training"
            >
              <StaticImage style={fsbgimg} src="../images/guy-working-at-computer.jpg" alt="" />
            </CardLink>
          </div>
        </div>
        <div tw="grid gap-12 my-24 md:(grid-cols-2)">
          <div tw="flex flex-col items-center text-center space-y-6">
            <HeartBubble tw="w-3/4 h-auto md:(w-1/2)" />
            <Heading level="h2" tw="text-2xl md:(text-5xl) text-black">
              Donors
            </Heading>
            <Text>
              <InternalLink to="/donors">View our Donors</InternalLink>
            </Text>
          </div>
          <div tw="flex flex-col items-center text-center space-y-6">
            <WavingBubble tw="w-3/4 h-auto md:(w-1/2)" />
            <Heading level="h2" tw="text-2xl md:(text-5xl) text-black">
              Volunteers
            </Heading>
            <Text>
              <InternalLink to="/volunteers">View our Volunteers</InternalLink>
            </Text>
          </div>
          {/* <div tw="flex flex-col items-center text-center space-y-6">
            <GraphBubble tw="w-3/4 h-auto md:(w-1/2)" />
            <Heading level="h2" tw="text-2xl md:(text-5xl) text-black">
              Financials
            </Heading>
            <Text>
              <InternalLink to="/financials">View our Financials</InternalLink>
            </Text>
          </div> */}
        </div>
        <Heading level="h1" tw="text-6xl">
          <span tw="text-black">In Memoriam:</span> <span tw="text-blue">Liane Regendanz</span>
        </Heading>
        <div tw="grid gap-6 items-end my-24 lg:(grid-cols-4 grid-rows-2)">
          <div tw="hidden lg:(col-span-1 row-span-2 block)">
            <StaticImage style={roundedImg} src="../images/liane-regendanz.jpg" alt="" />
          </div>
          <div tw="relative z-20 lg:(col-span-3)">
            <div tw="rounded-lg bg-blue text-white p-6 lg:(-ml-20 -mb-20) xl:(-ml-20 -mb-10)">
              <Heading level="h3" tw="mt-0">
                A life of compassion, community & opportunity
              </Heading>
              <Text>
                It is with overwhelming sadness that we write of the passing of Liane Regendanz,
                former Executive Director of St. Stephen’s Community House. Liane’s impact on St.
                Stephen’s and all the people who came through our doors was profound.
              </Text>
              <Text>
                <InternalLink
                  tw="text-white hover:(text-blue-50)"
                  to="/in-memoriam-liane-regendanz"
                >
                  Continue Reading In Memoriam: Liane Regendanz
                </InternalLink>
              </Text>
            </div>
          </div>
          <div tw="lg:(col-span-1 row-span-1)">
            <StaticImage
              src="../images/people-out-infront-of-a-building.jpg"
              alt=""
              layout="fullWidth"
              style={roundedImg}
            />
          </div>
          <div tw="lg:(col-span-1 row-span-1)">
            <StaticImage
              src="../images/standing-behind-counter.jpg"
              alt=""
              layout="fullWidth"
              style={roundedImg}
            />
          </div>
          <div tw="lg:(col-span-1 row-span-1)">
            <StaticImage
              src="../images/two-people-happy.jpg"
              alt=""
              layout="fullWidth"
              style={roundedImg}
            />
          </div>
        </div>
      </div>
      <div tw="relative my-24">
        <StaticImage
          src="../images/board-members.jpg"
          alt=""
          style={fsbgimg}
          layout="fullWidth"
        />
        <div tw="container relative z-20 py-64">
          <Heading level="h2" tw="my-0 text-white text-6xl">
            Board of Directors
          </Heading>
          <Text>
            <InternalLink to="/board-directors" tw="text-white hover:(text-blue-50)">
              View our Board of Directors
            </InternalLink>
          </Text>
        </div>
      </div>
    </Layout>
  );
};

export default IndexPage;
